// Pure helpers for the "Email these questions" dialog (kanban bafe8216),
// kept out of the component file so the pieces are testable on their own.
import type { DigestLedgerRow, DigestPreview, DigestPreviewResponse } from '../../services/roadmapReports';

/** The ledger row for a recipient in the current report, if the server has one. */
export function ledgerFor(rows: DigestLedgerRow[], d: DigestPreview): DigestLedgerRow | null {
  return rows.find((r) => r.kind === d.kind && r.recipient_name === d.recipient_name) ?? null;
}

/** True when "Send the emails" would (re)try this recipient: nothing on the
 * ledger yet, or a row a run recorded as skipped / failed (kanban 2e422cc2). */
export function stillOwed(rows: DigestLedgerRow[], d: DigestPreview): boolean {
  const row = ledgerFor(rows, d);
  return !row || row.status === 'skipped' || row.status === 'failed';
}

/** A ledger reason in the reader's words: no feature-switch names in the UI. */
export function plainDigestReason(detail: string | null | undefined): string | null {
  if (!detail) return null;
  if (/enable_roadmap_question_digest is off/i.test(detail)) return 'automatic sending is switched off';
  if (/frequency.*off|set to off/i.test(detail)) return 'email frequency is set to off for this person';
  return detail;
}

export type DigestRowTone = 'sent' | 'ready' | 'skipped' | 'pending';

/**
 * What the row says, which is what pressing Send would do NOW, not what the
 * last run recorded: the list used to read "skipped" on twelve rows a run had
 * skipped while the footer said six emails would go out.
 */
export function digestRowState(
  rows: DigestLedgerRow[],
  d: DigestPreview,
  offNames: ReadonlySet<string> = new Set(),
): { label: string; tone: DigestRowTone; note: string | null; canSend: boolean } {
  const row = ledgerFor(rows, d);
  if (row?.status === 'sent') return { label: 'sent', tone: 'sent', note: null, canSend: Boolean(d.recipient_email) };
  if (offNames.has(d.recipient_name)) {
    return { label: 'off', tone: 'skipped', note: 'Email frequency is off for this person. Change it above to send.', canSend: false };
  }
  if (!d.recipient_email) return { label: 'no address', tone: 'skipped', note: 'No email address on file. Set one above.', canSend: false };
  if (row?.status === 'pending') return { label: 'sending', tone: 'pending', note: null, canSend: false };
  const last = row ? plainDigestReason(row.detail) : null;
  return { label: 'will send', tone: 'ready', note: last ? `Not sent by the last run: ${last.replace(/[.\s]+$/, '')}.` : null, canSend: true };
}

/** One line saying what Send will do; it counts the same rows digestRowState marks "will send". */
export function sendSummary(preview: DigestPreviewResponse | null, offNames: ReadonlySet<string> = new Set()): string {
  if (!preview) return '';
  const states = preview.digests.map((d) => digestRowState(preview.already, d, offNames));
  const count = (label: string) => states.filter((s) => s.label === label).length;
  const parts: string[] = [];
  const ready = count('will send');
  if (ready) parts.push(`${ready} email${ready === 1 ? '' : 's'} will go out`);
  if (count('no address')) parts.push(`${count('no address')} skipped for no address`);
  if (count('off')) parts.push(`${count('off')} set to off`);
  if (count('sent')) parts.push(`${count('sent')} already sent for this report`);
  return parts.join(' · ') || 'Nothing to send';
}
